import React, { useState, useRef, useEffect } from 'react'
import ReactDOM from 'react-dom'
import { useTranslation } from 'react-i18next'
import useSEO from '../hooks/useSEO'

// import studio1 from '../assets/studio/1.jpg'
// import studio2 from '../assets/studio/2.jpg'
import studio3 from '../assets/studio/3.webp'
import studio4 from '../assets/studio/4.webp'
import studio5 from '../assets/studio/5.webp'
import studio6 from '../assets/studio/6.webp'
import studio7 from '../assets/studio/7.webp'

// Componente LazyImage riutilizzabile
const LazyImage = ({
  src,
  alt,
  className,
  style,
  onClick,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  forwardRef,
  loading = "lazy"
}: {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: (e: React.MouseEvent) => void;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: (e: React.TouchEvent) => void;
  forwardRef?: React.RefObject<HTMLImageElement>;
  loading?: "lazy" | "eager";
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  const actualRef = forwardRef || imgRef;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px',
      }
    );

    if (actualRef.current) {
      observer.observe(actualRef.current);
    }

    return () => observer.disconnect();
  }, [actualRef]);

  return (
    <div className="relative w-full h-full">
      {/* Placeholder mentre l'immagine si carica */}
      {!imageLoaded && (
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse flex items-center justify-center">
          <div className="w-16 h-16 bg-gray-300 rounded-full animate-pulse"></div>
        </div>
      )}
      
      <img
        ref={actualRef}
        src={isVisible ? src : undefined}
        alt={alt}
        className={`transition-opacity duration-500 ${
          imageLoaded ? 'opacity-100' : 'opacity-0'
        } ${className || ''}`}
        style={style}
        loading={loading}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageLoaded(true)}
        onClick={onClick}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      />
    </div>
  );
};

const studioImages = [
  // studio1,
  // studio2,
  studio3,
  studio4,
  studio5,
  studio6,
  studio7,
]

function LoStudio() {
  const { t } = useTranslation();

  // SEO Configuration
  useSEO({
    title: t('loStudio.seo.title'),
    description: t('loStudio.seo.description'),
    keywords: t('loStudio.seo.keywords'),
    schema: {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      "name": "Studio Dentistico Sgarbini",
      "description": t('loStudio.seo.schemaDescription'),
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Via Camilla 10/a",
        "addressLocality": "Roma",
        "addressCountry": "IT"
      },
      "telephone": "+39 06 7886902",
      "amenityFeature": [
        {
          "@type": "LocationFeatureSpecification",
          "name": t('loStudio.seo.amenities.modern'),
          "value": true
        },
        {
          "@type": "LocationFeatureSpecification", 
          "name": t('loStudio.seo.amenities.technology'),
          "value": true
        },
        {
          "@type": "LocationFeatureSpecification",
          "name": t('loStudio.seo.amenities.comfort'),
          "value": true
        }
      ]
    }
  });

  // Slideshow state
  const [slideshowOpen, setSlideshowOpen] = useState(false)
  const [currentIndex, setCurrentIndex] = useState(0)

  // For swipe transition
  const [swipeOffset, setSwipeOffset] = useState(0)
  const [isSwiping, setIsSwiping] = useState(false)
  const swipeImgRef = useRef<HTMLImageElement>(null)

  // Images for the gallery (exclude image 5)
  const galleryImages = studioImages;//.filter((_, idx) => idx !== 4)

  const openSlideshow = (idx: number) => {
    setCurrentIndex(idx)
    setSlideshowOpen(true)
  }

  const closeSlideshow = () => setSlideshowOpen(false)

  const prevImage = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    setCurrentIndex((prev) => (prev === 0 ? galleryImages.length - 1 : prev - 1))
  }

  const nextImage = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation()
    setCurrentIndex((prev) => (prev === galleryImages.length - 1 ? 0 : prev + 1))
  }

  // Touch swipe state
  const touchStartX = useRef<number | null>(null)
  const touchEndX = useRef<number | null>(null)

  // Handle touch events for swipe
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX
    setIsSwiping(true)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchStartX.current !== null) {
      const offset = e.touches[0].clientX - touchStartX.current
      setSwipeOffset(offset)
    }
    touchEndX.current = e.touches[0].clientX
  }

  const handleTouchEnd = () => {
    setIsSwiping(false)
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const delta = touchStartX.current - touchEndX.current
      if (Math.abs(delta) > 50) {
        setSwipeOffset(0)
        if (delta > 0) {
          // Swipe left
          nextImage()
        } else {
          // Swipe right
          prevImage()
        }
      } else {
        // Not enough swipe, reset
        setSwipeOffset(0)
      }
    } else {
      setSwipeOffset(0)
    }
    touchStartX.current = null
    touchEndX.current = null
  }

  // Keyboard navigation
  React.useEffect(() => {
    if (!slideshowOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prevImage()
      if (e.key === 'ArrowRight') nextImage()
      if (e.key === 'Escape') closeSlideshow()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
    // eslint-disable-next-line
  }, [slideshowOpen, currentIndex])

  // Lock body scroll when slideshow is open
  React.useEffect(() => {
    if (slideshowOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => { document.body.style.overflow = '' }
  }, [slideshowOpen])

  // Infinite scroll state
  const IMAGES_PER_PAGE = 4
  const [visibleCount, setVisibleCount] = useState(IMAGES_PER_PAGE)
  const galleryContainerRef = useRef<HTMLDivElement>(null)

  // Load more images when scrolling near the bottom
  useEffect(() => {
    const handleScroll = () => {
      if (!galleryContainerRef.current) return
      const rect = galleryContainerRef.current.getBoundingClientRect()
      if (rect.bottom <= window.innerHeight + 100) {
        setVisibleCount((prev) =>
          Math.min(prev + IMAGES_PER_PAGE, galleryImages.length)
        )
      }
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [galleryImages.length])

  // Animazione fade-in per la gallery e per header/testo/immagine 5
  const [galleryVisible, setGalleryVisible] = useState(false)
  const [headerVisible, setHeaderVisible] = useState(false)

  useEffect(() => {
    // Ritardo per effetto fade-in
    const timeout1 = setTimeout(() => setHeaderVisible(true), 100)
    const timeout2 = setTimeout(() => setGalleryVisible(true), 400)
    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
    }
  }, [])

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 z-0">
      {/* Desktop: text + image 5 side by side, text fades into image.
          Mobile: image 5 above text */}
      <div
        className={`
          flex flex-col md:flex-row md:items-stretch md:gap-10 mb-10
          transition-all duration-700
          ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        `}
      >
        {/* Image 5: mobile first, then desktop right */}
        <div className="w-full md:w-1/2 mb-6 md:mb-0 md:order-2 flex">
          <div
            className={`
              relative z-0 h-64 md:h-auto rounded-xl md:rounded-none overflow-hidden shadow-md md:shadow-none flex-1 flex
              transition-all duration-700
              ${headerVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
            `}
            style={{ transitionDelay: '150ms' }}
          >
            <LazyImage
              src={studio5}
              alt={t('loStudio.images.alt') + ' 5'}
              className="w-full h-full object-cover"
              style={{ objectPosition: "center" }}
            />
            {/* Gradient fade for text overlap on desktop */}
            <div className="hidden md:block absolute left-0 top-0 h-full w-1/2 bg-gradient-to-l from-transparent to-white pointer-events-none" />
          </div>
        </div>
        {/* Text */}
        <div
          className={`
            w-full md:w-1/2 flex flex-col justify-center
            transition-all duration-700
            ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
          `}
          style={{ transitionDelay: '250ms' }}
        >
          <h1
            className={`
              text-4xl md:text-5xl font-bold text-center md:text-left mb-8
              transition-all duration-700
              ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-8'}
            `}
            style={{ transitionDelay: '350ms' }}
          >
            {t('loStudio.title')}
          </h1>
          <div
            className={`
              text-lg text-gray-700 leading-relaxed space-y-4
              transition-all duration-700
              ${headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
            `}
            style={{ transitionDelay: '450ms' }}
            id='loStudioDescription'
          >
            <p>
              {t('loStudio.description.paragraph1')}
            </p>
            {/* <p>
              {t('loStudio.description.paragraph2')}
            </p> */}
            <p>
              {t('loStudio.description.paragraph3')}
            </p>
          </div>
        </div>
      </div>
      {/* Gallery: all images except image 5 */}
      <div
        className={`
          grid grid-cols-1 sm:grid-cols-2 gap-6
          transition-all duration-700
          ${galleryVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}
        `}
        ref={galleryContainerRef}
      >
        {galleryImages.slice(0, visibleCount).map((src, idx) => (
          <button
            key={idx}
            className="rounded-xl overflow-hidden shadow-md focus:outline-none transition-all duration-700"
            style={{
              transitionDelay: `${galleryVisible ? idx * 80 : 0}ms`
            }}
            onClick={() => openSlideshow(idx)}
            tabIndex={0}
            aria-label={`${t('loStudio.images.slideshow.open')} ${idx + 1}`}
            type="button"
          >
            <LazyImage
              src={src}
              alt={`${t('loStudio.images.alt')} ${idx < 4 ? idx + 1 : idx + 2}`}
              className="w-full h-80 object-cover transition-transform duration-300 hover:scale-105"
            />
          </button>
        ))}
      </div>

      {/* Slideshow Modal */}
      {slideshowOpen &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 z-[11] flex items-center justify-center bg-black bg-opacity-80"
            onClick={closeSlideshow}
          >
            <button
              className="absolute top-4 right-4 text-white text-3xl font-bold z-10"
              onClick={closeSlideshow}
              aria-label={t('loStudio.images.slideshow.close')}
              type="button"
            >
              &times;
            </button>
            <div className="flex items-center justify-center w-full h-full">
              <div className="relative flex items-center">
                {/* Desktop arrows */}
                <button
                  className="hidden sm:flex absolute left-0 top-0 bottom-0 items-center justify-center text-white text-4xl z-10 w-16 bg-black bg-opacity-0 hover:bg-opacity-20 transition"
                  style={{ height: '100%' }}
                  onClick={prevImage}
                  aria-label={t('loStudio.images.slideshow.previous')}
                  type="button"
                >
                  <span className="flex items-center justify-center w-full h-full leading-none p-0">{'<'}</span>
                </button>
                <LazyImage
                  src={galleryImages[currentIndex]}
                  alt={`${t('loStudio.images.slideshow.current')} ${currentIndex + 1}`}
                  className="max-h-[80vh] max-w-[90vw] rounded-xl shadow-2xl object-contain"
                  onClick={e => e.stopPropagation()}
                  style={{
                    display: 'block',
                    touchAction: 'pan-y',
                    transform: `translateX(${swipeOffset}px)`,
                    transition: isSwiping ? 'none' : 'transform 0.3s cubic-bezier(.4,0,.2,1)'
                  }}
                  onTouchStart={handleTouchStart}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={handleTouchEnd}
                  forwardRef={swipeImgRef}
                  loading="eager"
                />
                <button
                  className="hidden sm:flex absolute right-0 top-0 bottom-0 items-center justify-center text-white text-4xl z-10 w-16 bg-black bg-opacity-0 hover:bg-opacity-20 transition"
                  style={{ height: '100%' }}
                  onClick={nextImage}
                  aria-label={t('loStudio.images.slideshow.next')}
                  type="button"
                >
                  <span className="flex items-center justify-center w-full h-full leading-none p-0">{'>'}</span>
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}

export default LoStudio
